import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent,
  type RefObject,
  type SVGProps,
} from 'react';
import { Link, useLocation, useMatch, useSearchParams } from 'react-router';
import {
  useGetChatMatterQuery,
  useGetMatterAboutQuery,
  useLazyGetChatHistoryQuery,
  usePostEventChatAbandonedMutation,
  type ChatHistoryData,
} from '@/api';
import { AccessControlAction, AppRoutes, MERMAIDD_URL } from '@/constants';
import { useAccessControl, useEventCallback, useOrganisationName, useResizeObserver } from '@/hooks';
import { useLogout } from '@/hooks/useLogout';
import {
  ActionsIcon,
  AerialsIcon,
  CommentIcon,
  DocumentsIcon,
  DownChevronIcon,
  ExternalLinkIcon,
  LogoIconFletch as LogoIcon,
  MermaiddIcon,
  PinIcon,
  ScalesIcon,
  Tooltip,
  UnpinIcon,
  UsersIcon,
  TOOLTIP_SURFACE_BOTTOM,
  TOOLTIP_SURFACE_SIDE,
} from '@repo/atomic-components';
import { UserProfileMenu } from './UserProfileMenu';
import { setSidebarMode, useSidebarMode, type SidebarMode } from './sidebarMode';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';
import { useVisibleActions } from '@/ui/pages/ActionsPage/useVisibleActions';
import { withoutProjectListParams } from '@/ui/pages/ProjectListPage/utils/projectListFilters';
import {
  clearChat,
  selectChatId,
  selectLastResponseAt,
  setIgnoreSharedLinkChatIdSync,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { classNames } from 'primereact/utils';

const CHAT_ABANDONED_THRESHOLD_MS = 30_000;
// rem so the sidebar grows with the text size preference.
const SIDEBAR_COLLAPSED_WIDTH = '3.5rem';
const SIDEBAR_EXPANDED_WIDTH = '16.25rem';
// One `/chats` call feeds both recent chats and recent case refs. Fetch extra so case refs can be de-duplicated.
const CHAT_HISTORY_FETCH_LIMIT = 40;
const RECENT_CHATS_LIMIT = 20;
const RECENT_CASE_REFS_LIMIT = 3;

/** Whether a nav row's route is the current page. Query string is ignored. `end` = exact match only. */
function useIsNavActive(to: string, end = false) {
  const pathOnly = to.split('?')[0];
  return !!useMatch({ path: pathOnly, end });
}

/** Modified and middle clicks open a new tab or window, so they shouldn't change this page. */
const isPlainLeftClick = (event: MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

type SidebarNavItem = {
  /** Active only on an exact URL match, not on sub-routes. */
  end?: boolean;
  label: string;
  to: string;
  tooltip: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  forceActive?: boolean;
};

function SidebarNavLink({
  item,
  expanded,
  showViewAll = false,
}: {
  item: SidebarNavItem;
  expanded: boolean;
  /** Renders the row as a section header with a "View all >" hint on hover. */
  showViewAll?: boolean;
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  // Collapsed mode has no sub-items, so the parent icon stays active on any sub-route.
  const routeActive = useIsNavActive(item.to, !!item.end && expanded);
  const isActive = routeActive || !!item.forceActive;
  return (
    <>
      <Link
        ref={ref}
        to={item.to}
        aria-label={item.tooltip}
        aria-current={isActive ? 'page' : undefined}
        className={classNames(
          'focus-visible:outline-fletchers-green-500 flex w-full cursor-pointer items-center text-sm',
          expanded ? 'h-8 gap-2 rounded-md p-2' : 'group h-14 justify-center',
          showViewAll && expanded && 'group',
          expanded &&
            (isActive
              ? 'nav-active-glass text-theme-dark-green border-theme-border-grey/50 border font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
              : 'text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 border border-transparent transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]'),
          !expanded && (isActive ? 'text-theme-dark-green' : 'text-theme-black'),
        )}
      >
        {expanded ? (
          <item.Icon aria-hidden width={18} height={18} className="shrink-0" />
        ) : (
          <span
            className={classNames(
              'flex h-11 w-11 items-center justify-center rounded-lg',
              isActive
                ? 'nav-active-glass border-theme-border-grey/50 border shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
                : 'group-hover:!bg-theme-white/90 group-hover:border-theme-border-grey/50 border border-transparent transition-shadow group-hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
            )}
          >
            <item.Icon aria-hidden width={18} height={18} className="shrink-0" />
          </span>
        )}
        {expanded && <span className={classNames('truncate', showViewAll && 'font-semibold')}>{item.label}</span>}
        {expanded && showViewAll && (
          <span
            aria-hidden
            className={classNames(
              'ml-auto flex items-center gap-2 text-[0.8125rem] font-normal opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
              !isActive && 'text-theme-text-grey',
            )}
          >
            view all
            <DownChevronIcon className="-rotate-90" width={12} height={12} />
          </span>
        )}
      </Link>
      {!expanded && (
        <Tooltip
          target={ref as RefObject<HTMLElement>}
          content={item.tooltip}
          position="right"
          className={TOOLTIP_SURFACE_SIDE}
          baseZIndex={10000}
        />
      )}
    </>
  );
}

function PlusInCircle({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={classNames(
        'bg-theme-mid-grey/35 [body.theme-soft_&]:bg-theme-border-grey/35 flex size-[1.125rem] shrink-0 items-center justify-center rounded-full text-lg leading-none',
        className,
      )}
    >
      +
    </span>
  );
}

/** Link to the Mermaidd case list, opened in a new tab. */
function MermaiddButton({ expanded }: { expanded: boolean }) {
  const ref = useRef<HTMLAnchorElement>(null);
  return (
    <>
      <a
        ref={ref}
        href={MERMAIDD_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Mermaidd (opens in a new tab)"
        className={classNames(
          'group focus-visible:outline-fletchers-green-500 text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 flex w-full cursor-pointer items-center border border-transparent text-sm transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
          expanded ? 'h-8 gap-2 rounded-md p-2 text-left' : 'h-14 justify-center',
        )}
      >
        <MermaiddIcon width={18} height={18} className="shrink-0" />
        {expanded && <span className="truncate">Mermaidd</span>}
        {expanded && (
          <span
            aria-hidden
            className="text-theme-text-grey ml-auto flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          >
            <ExternalLinkIcon width={12} height={12} />
          </span>
        )}
      </a>
      {!expanded && (
        <Tooltip
          target={ref as RefObject<HTMLElement>}
          content="Mermaidd"
          position="right"
          className={TOOLTIP_SURFACE_SIDE}
          baseZIndex={10000}
        />
      )}
    </>
  );
}

function NewChatRow({ expanded, onNewChat }: { expanded: boolean; onNewChat: (event: MouseEvent) => void }) {
  const ref = useRef<HTMLAnchorElement>(null);
  const { pathname } = useLocation();
  // Active on the new-chat page (`/chat` exactly).
  const isActive = pathname === `/${AppRoutes.Chat}`;
  return (
    <>
      <Link
        ref={ref}
        to={`/${AppRoutes.Chat}`}
        onClick={onNewChat}
        aria-label="Start a new chat"
        aria-current={isActive ? 'page' : undefined}
        className={classNames(
          'focus-visible:outline-fletchers-green-500 flex h-8 w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left text-[0.875rem]',
          isActive
            ? 'nav-active-glass text-theme-dark-green border-theme-border-grey/50 border font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
            : 'text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 border border-transparent transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
        )}
      >
        <PlusInCircle />
        {expanded && <span className="truncate">New chat</span>}
      </Link>
      {!expanded && (
        <Tooltip
          target={ref as RefObject<HTMLElement>}
          content="Start a new chat"
          position="right"
          className={TOOLTIP_SURFACE_SIDE}
          baseZIndex={10000}
        />
      )}
    </>
  );
}

/** Scrolls overflowing row text back and forth while hovered (Web Animations API). */
function useMarqueeOnHover() {
  const clipRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [hovered, setHovered] = useState(false);

  const onMouseEnter = () => {
    if (clipRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - clipRef.current.clientWidth;
      setOverflow(Math.max(0, diff));
    }
    setHovered(true);
  };
  const onMouseLeave = () => setHovered(false);

  useEffect(() => {
    if (!hovered || overflow <= 0 || !textRef.current) return;
    const scrollMs = Math.max(1500, overflow * 25);
    const leadingPauseMs = 300;
    const trailingPauseMs = 600;
    const totalMs = scrollMs + leadingPauseMs + trailingPauseMs;
    const anim = textRef.current.animate(
      [
        { transform: 'translateX(0)', offset: 0 },
        { transform: 'translateX(0)', offset: leadingPauseMs / totalMs },
        { transform: `translateX(-${overflow}px)`, offset: (leadingPauseMs + scrollMs) / totalMs },
        { transform: `translateX(-${overflow}px)`, offset: 1 },
      ],
      { duration: totalMs, iterations: Infinity, easing: 'linear' },
    );
    return () => anim.cancel();
  }, [hovered, overflow]);

  return { clipRef, textRef, onMouseEnter, onMouseLeave };
}

const CHATS_SCROLLER_ATTR = 'data-sidebar-chats';
/** Height of the recent chats list's edge fades. */
const CHATS_FADE_PX = 60;

/** Recent chat row. Long text scrolls on hover. */
function RecentChatRow({ chat, currentSearchParams }: { chat: ChatHistoryData; currentSearchParams: string }) {
  const { clipRef, textRef, onMouseEnter, onMouseLeave } = useMarqueeOnHover();
  const to = `/${AppRoutes.Chat}/${chat.chatId}${currentSearchParams ? `?${currentSearchParams}` : ''}`;
  const isActive = useIsNavActive(to);
  const rowRef = useRef<HTMLAnchorElement>(null);

  // Keep the open chat out of the list's edge fades, where it would look disabled.
  useEffect(() => {
    const row = rowRef.current;
    const scroller = row?.closest<HTMLElement>(`[${CHATS_SCROLLER_ATTR}]`);
    if (!isActive || !row || !scroller) return;
    const top = row.offsetTop - scroller.offsetTop;
    const bottom = top + row.offsetHeight;
    if (bottom > scroller.scrollTop + scroller.clientHeight - CHATS_FADE_PX) {
      scroller.scrollTo({ top: bottom - scroller.clientHeight + CHATS_FADE_PX });
    } else if (scroller.scrollTop > 0 && top < scroller.scrollTop + CHATS_FADE_PX) {
      scroller.scrollTo({ top: Math.max(0, top - CHATS_FADE_PX) });
    }
  }, [isActive]);

  return (
    <Link
      ref={rowRef}
      to={to}
      aria-current={isActive ? 'page' : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={classNames(
        'focus-visible:outline-fletchers-green-500 flex h-8 w-full cursor-pointer items-center rounded-md p-2 text-[0.8125rem]',
        isActive
          ? 'nav-active-glass text-theme-dark-green border-theme-border-grey/50 border font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
          : 'text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 border border-transparent transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
      )}
    >
      <span ref={clipRef} className="block min-w-0 flex-1 overflow-hidden text-left">
        <span ref={textRef} className="inline-block whitespace-nowrap">
          {chat.title}
          {chat.caseRef && (
            <span className={classNames('ml-1 font-normal', !isActive && 'text-theme-text-grey')}>{chat.caseRef}</span>
          )}
        </span>
      </span>
    </Link>
  );
}

/**
 * Recent case-ref row. Resolves `matterId` via `getChatMatter`, then `clientName` via
 * `getMatterAbout`, and links to `/cases/{matterId}` (or `/cases` while loading).
 */
function CaseRefRow({
  caseRef,
  chatId,
  currentSearchParams,
}: {
  caseRef: string;
  chatId: string;
  currentSearchParams: string;
}) {
  const { data: matter } = useGetChatMatterQuery({ chatId });
  const { data: matterAbout } = useGetMatterAboutQuery(
    { matterId: matter?.matterId ?? '' },
    { skip: !matter?.matterId },
  );
  const clientName = matterAbout?.info.clientName;

  const query = currentSearchParams ? `?${currentSearchParams}` : '';
  const to = matter?.matterId ? `/${AppRoutes.Cases}/${matter.matterId}${query}` : `/${AppRoutes.Cases}${query}`;

  const { clipRef, textRef, onMouseEnter, onMouseLeave } = useMarqueeOnHover();
  const isActive = useIsNavActive(to);

  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={classNames(
        'focus-visible:outline-fletchers-green-500 flex h-8 w-full cursor-pointer items-center rounded-md p-2 text-[0.8125rem]',
        isActive
          ? 'nav-active-glass text-theme-dark-green border-theme-border-grey/50 border font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
          : 'text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 border border-transparent transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
      )}
    >
      <span ref={clipRef} className="block min-w-0 flex-1 overflow-hidden text-left">
        <span ref={textRef} className="inline-block whitespace-nowrap">
          {caseRef}
          {clientName && (
            <span className={classNames('ml-1 font-normal', !isActive && 'text-theme-text-grey')}>{clientName}</span>
          )}
        </span>
      </span>
    </Link>
  );
}

function ChatsSectionHeader({
  expanded,
  historyHref,
  onNewChat,
}: {
  expanded: boolean;
  historyHref: string;
  onNewChat: (event: MouseEvent) => void;
}) {
  const commentRef = useRef<HTMLAnchorElement>(null);
  const isActive = useIsNavActive(historyHref);
  // Collapsed: the Chats icon is active on any chat or history page.
  const { pathname } = useLocation();
  const chatsCollapsedActive =
    pathname === `/${AppRoutes.Chat}` ||
    pathname.startsWith(`/${AppRoutes.Chat}/`) ||
    pathname === `/${AppRoutes.History}` ||
    pathname.startsWith(`/${AppRoutes.History}/`);

  if (!expanded) {
    // Collapsed: just the chat icon square. New chat and the list need the expanded state.
    return (
      <>
        <Link
          ref={commentRef}
          to={historyHref}
          aria-label="Chats"
          aria-current={chatsCollapsedActive ? 'page' : undefined}
          className={classNames(
            'group focus-visible:outline-fletchers-green-500 flex h-14 w-full cursor-pointer items-center justify-center',
            chatsCollapsedActive ? 'text-theme-dark-green' : 'text-theme-black',
          )}
        >
          <span
            className={classNames(
              'flex h-11 w-11 items-center justify-center rounded-lg',
              chatsCollapsedActive
                ? 'nav-active-glass border-theme-border-grey/50 border shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
                : 'group-hover:!bg-theme-white/90 group-hover:border-theme-border-grey/50 border border-transparent transition-shadow group-hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
            )}
          >
            <CommentIcon aria-hidden width={18} height={18} className="shrink-0" />
          </span>
        </Link>
        <Tooltip
          target={commentRef as RefObject<HTMLElement>}
          content="Chats"
          position="right"
          className={TOOLTIP_SURFACE_SIDE}
          baseZIndex={10000}
        />
      </>
    );
  }

  return (
    <>
      <Link
        to={historyHref}
        aria-current={isActive ? 'page' : undefined}
        className={classNames(
          'group focus-visible:outline-fletchers-green-500 flex h-8 cursor-pointer items-center gap-2 rounded-md p-2 text-sm',
          isActive
            ? 'nav-active-glass text-theme-dark-green border-theme-border-grey/50 border font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-xl backdrop-saturate-[1.4]'
            : 'text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 border border-transparent transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
        )}
      >
        <CommentIcon aria-hidden width={18} height={18} className="shrink-0" />
        <span className="font-semibold">Chats</span>
        <span
          aria-hidden
          className={classNames(
            'ml-auto flex items-center gap-2 text-[0.8125rem] font-normal opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
            !isActive && 'text-theme-text-grey',
          )}
        >
          view all
          <DownChevronIcon className="-rotate-90" width={12} height={12} />
        </span>
      </Link>
      <NewChatRow expanded onNewChat={onNewChat} />
    </>
  );
}

function ChatsSectionList({
  recentChats,
  currentSearchParams,
}: {
  recentChats: ChatHistoryData[];
  currentSearchParams: string;
}) {
  if (recentChats.length === 0) return null;
  return (
    <>
      {recentChats.map((chat) => (
        <RecentChatRow key={chat.chatId} chat={chat} currentSearchParams={currentSearchParams} />
      ))}
    </>
  );
}

export const Sidebar = () => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Hide each edge fade when the chats list is scrolled to that edge.
  const [chatsAtTop, setChatsAtTop] = useState(true);
  const [chatsAtBottom, setChatsAtBottom] = useState(false);
  const updateChatsEdges = (el: HTMLElement) => {
    setChatsAtTop(el.scrollTop <= 1);
    setChatsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  };
  const handleChatsScroll = (event: React.UIEvent<HTMLDivElement>) => updateChatsEdges(event.currentTarget);
  // Also measured on resize, so a list short enough to fit gets no bottom fade.
  const [chatsScroller, setChatsScroller] = useState<HTMLDivElement | null>(null);
  const [chatsList, setChatsList] = useState<HTMLDivElement | null>(null);
  const handleChatsResize = useEventCallback(() => {
    if (chatsScroller) updateChatsEdges(chatsScroller);
  });
  useResizeObserver(chatsScroller, undefined, handleChatsResize);
  useResizeObserver(chatsList, undefined, handleChatsResize);
  const chatsTopStop = chatsAtTop ? 'black 0' : `transparent 0, black ${CHATS_FADE_PX}px`;
  const chatsBottomStop = chatsAtBottom ? 'black 100%' : `black calc(100% - ${CHATS_FADE_PX}px), transparent 100%`;
  const chatsScrollMask =
    chatsAtTop && chatsAtBottom ? undefined : `linear-gradient(to bottom, ${chatsTopStop}, ${chatsBottomStop})`;
  const frostedGlassClass = useFrostedGlassClass();
  // Sidebar mode: 'overlay' (collapses when the pointer leaves) or 'fixed' (always expanded).
  // Shared store because the settings hub sets it too.
  const mode = useSidebarMode();
  const toggleMode = () => {
    const next: SidebarMode = mode === 'overlay' ? 'fixed' : 'overlay';
    setSidebarMode(next);
    // Collapse right away on unpin, otherwise it stays open until the pointer leaves.
    if (next === 'overlay') {
      setHovered(false);
      setFocused(false);
    }
  };
  const logoRef = useRef<HTMLAnchorElement>(null);
  const pinButtonRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  // The new-chat page always shows the sidebar expanded.
  const isNewChatRoute = pathname === `/${AppRoutes.Chat}`;
  const expanded = isNewChatRoute || mode === 'fixed' || hovered || focused || menuOpen;
  // Fixed mode and the new-chat page take real layout width; overlay mode reserves only the collapsed width.
  const outerWidth = mode === 'fixed' || isNewChatRoute ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentSearchParams = withoutProjectListParams(searchParams, pathname).toString();
  const storeChatId = useAppSelector(selectChatId);
  const lastResponseAt = useAppSelector(selectLastResponseAt);
  const isViewUserManagementTab = useAccessControl(AccessControlAction.VIEW_USER_MANAGEMENT_TAB);
  const handleLogout = useLogout();
  const [postEventChatAbandoned] = usePostEventChatAbandonedMutation();

  // Organisation name, or `null` for non-global-admins — the footer then shows only the user name.
  const organisationName = useOrganisationName();

  // Recent chats, fetched on mount so they're ready when the sidebar expands.
  const [triggerGetChatHistory, { data: chatHistoryData }] = useLazyGetChatHistoryQuery();
  const fetchRecentChats = useEventCallback(() => {
    // If `/chats` fails, the sections just don't render.
    triggerGetChatHistory({
      pageSize: CHAT_HISTORY_FETCH_LIMIT,
      sortBy: 'latest_activity',
      sortDirection: 'descending',
    })
      .unwrap()
      .catch(() => {});
  });

  // Refetch when a message is sent or answered. Keyed on the sends: `clearChat` replaces the object on every new chat.
  const pendingMessages = useAppSelector((state) => state.chat.pendingMessages);
  const pendingSendsKey = useAppSelector((state) =>
    Object.entries(state.chat.pendingMessages)
      .flatMap(([chatId, entry]) => (entry ? [`${chatId}@${entry.createdAt}`] : []))
      .join(),
  );
  useEffect(() => {
    fetchRecentChats();
  }, [fetchRecentChats, pendingSendsKey]);

  // Refetch when the open chat gets or changes its title. Opening or leaving a chat isn't a title change.
  const currentChatId = useAppSelector((state) => state.chat.chatData?.chatId);
  const currentChatTitle = useAppSelector((state) => state.chat.chatData?.chatTitle);
  const titledChatRef = useRef({ chatId: currentChatId, title: currentChatTitle });
  useEffect(() => {
    const previous = titledChatRef.current;
    titledChatRef.current = { chatId: currentChatId, title: currentChatTitle };
    if (previous.chatId !== currentChatId || previous.title === currentChatTitle) return;
    fetchRecentChats();
  }, [fetchRecentChats, currentChatId, currentChatTitle]);

  const allRecentChats = useMemo(() => chatHistoryData?.chats ?? [], [chatHistoryData?.chats]);
  // Show just-sent chats at the top right away, using the prompt as the title. Replaced by the real
  // chat once `/chats` returns it.
  const recentChats = useMemo(() => {
    const pendingEntries: ChatHistoryData[] = [];
    for (const [chatId, entry] of Object.entries(pendingMessages)) {
      if (!entry || !chatId) continue;
      pendingEntries.push({
        chatId,
        title: entry.userPrompt.trim() || 'New chat',
        lastUpdated: entry.createdAt,
        latestActivity: entry.createdAt,
        createdAt: entry.createdAt,
        user: '',
        userId: '',
        caseRef: '',
        isDraft: true,
      });
    }
    pendingEntries.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    const pendingIds = new Set(pendingEntries.map((p) => p.chatId));
    const serverEntries = allRecentChats.filter((c) => !pendingIds.has(c.chatId));
    return [...pendingEntries, ...serverEntries].slice(0, RECENT_CHATS_LIMIT);
  }, [allRecentChats, pendingMessages]);
  // Also keep the chat id each case ref came from, to resolve its matter via `getChatMatter`.
  const recentCaseRefs = useMemo(() => {
    const seen = new Set<string>();
    const refs: Array<{ caseRef: string; chatId: string }> = [];
    for (const chat of allRecentChats) {
      if (!chat.caseRef || seen.has(chat.caseRef)) continue;
      seen.add(chat.caseRef);
      refs.push({ caseRef: chat.caseRef, chatId: chat.chatId });
      if (refs.length === RECENT_CASE_REFS_LIMIT) break;
    }
    return refs;
  }, [allRecentChats]);

  // Home and New chat are links to `/chat`; a plain click also clears the current chat before the link navigates.
  const handleHomeClick = (event: MouseEvent) => {
    if (!isPlainLeftClick(event)) return;
    dispatch(setIgnoreSharedLinkChatIdSync(true));
    dispatch(clearChat());
  };

  const handleNewChat = (event: MouseEvent) => {
    if (!isPlainLeftClick(event)) return;
    if (storeChatId && lastResponseAt) {
      if (Date.now() - lastResponseAt <= CHAT_ABANDONED_THRESHOLD_MS) {
        postEventChatAbandoned({ chatId: storeChatId });
      }
    }
    dispatch(setIgnoreSharedLinkChatIdSync(true));
    dispatch(clearChat());
  };

  const navPath = (route: AppRoutes) => `/${route}?${currentSearchParams}`;
  const historyHref = `/${AppRoutes.History}${currentSearchParams ? `?${currentSearchParams}` : ''}`;

  const items = {
    cases: {
      label: 'Cases',
      to: navPath(AppRoutes.Cases),
      tooltip: 'View uploaded cases',
      Icon: ScalesIcon,
      end: true,
      forceActive: pathname.includes(AppRoutes.CaseHistory),
    },
    projects: {
      label: 'Projects',
      to: navPath(AppRoutes.Projects),
      tooltip: 'View projects',
      Icon: DocumentsIcon,
    },
    actions: {
      label: 'Actions',
      to: navPath(AppRoutes.Actions),
      tooltip: 'AI-powered actions',
      Icon: ActionsIcon,
    },
    aerials: {
      label: 'Aerials',
      to: navPath(AppRoutes.Aerials),
      tooltip: 'View aerials',
      Icon: AerialsIcon,
    },
    users: {
      label: 'Users',
      to: navPath(AppRoutes.UserManagement),
      tooltip: 'Manage users',
      Icon: UsersIcon,
    },
  } satisfies Record<string, SidebarNavItem>;

  const showUsers = isViewUserManagementTab;
  // Actions is hidden unless the user can perform at least one action.
  const visibleActions = useVisibleActions();
  const showActions = visibleActions.length > 0;

  return (
    <nav
      aria-label="Primary"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        const nextFocused = e.relatedTarget as Node | null;
        if (!nextFocused || !e.currentTarget.contains(nextFocused)) setFocused(false);
      }}
      // Clicking a nav item collapses the sidebar until the pointer leaves and comes back. The
      // profile avatar stops propagation, so it doesn't collapse. Opening an item in a new tab keeps it open.
      onClick={(e) => {
        if (!isPlainLeftClick(e)) return;
        setHovered(false);
        setFocused(false);
      }}
      className="relative h-full flex-none"
      style={{ width: outerWidth }}
    >
      <div
        className={classNames(
          frostedGlassClass,
          'border-theme-border-grey absolute inset-y-0 left-0 z-30 flex h-full flex-col overflow-hidden border-r transition-[width,box-shadow] duration-200',
          mode === 'overlay' && expanded && !isNewChatRoute && 'shadow-[8px_0_32px_rgba(0,0,0,0.18)]',
        )}
        style={{ width: expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH }}
      >
        <div className="border-theme-border-grey flex h-14 items-center border-b p-1">
          <div className="[&:hover:not(:has(button:hover))]:!bg-theme-white/90 [&:hover:not(:has(button:hover))]:border-theme-border-grey/50 flex h-full w-full items-center overflow-hidden rounded-md border border-transparent transition-shadow [&:hover:not(:has(button:hover))]:shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <Link
              ref={logoRef}
              to={`/${AppRoutes.Chat}`}
              onClick={handleHomeClick}
              // Links only activate on Enter; Home also takes Space, like a button.
              onKeyDown={(e) => {
                if (e.key !== ' ') return;
                e.preventDefault();
                e.currentTarget.click();
              }}
              aria-label="Home"
              className="focus-visible:outline-fletchers-green-500 flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md px-2"
            >
              <LogoIcon width="28" height="26" className="shrink-0" />
              <span
                className={classNames(
                  'text-theme-black font-inter text-[1.25rem] font-bold whitespace-nowrap transition-opacity',
                  expanded ? 'opacity-100' : 'opacity-0',
                )}
              >
                Fletch
              </span>
            </Link>
            {(hovered || focused) && !isNewChatRoute && (
              // Stops propagation so the click doesn't collapse the sidebar.
              <>
                <button
                  ref={pinButtonRef}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMode();
                  }}
                  aria-label={mode === 'overlay' ? 'Pin sidebar open' : 'Unpin sidebar'}
                  aria-pressed={mode === 'fixed'}
                  className="text-theme-text-grey hover:bg-theme-border-grey/70 hover:text-theme-black focus-visible:outline-fletchers-green-500 mr-2 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md"
                >
                  {mode === 'fixed' ? <UnpinIcon width={16} height={16} /> : <PinIcon width={16} height={16} />}
                </button>
                <Tooltip
                  target={pinButtonRef as RefObject<HTMLElement>}
                  content={mode === 'overlay' ? 'Pin sidebar open' : 'Unpin sidebar'}
                  position="bottom"
                  className={TOOLTIP_SURFACE_BOTTOM}
                />
              </>
            )}
          </div>
          {!expanded && (
            <Tooltip
              target={logoRef as RefObject<HTMLElement>}
              content="Home"
              position="right"
              className={TOOLTIP_SURFACE_SIDE}
              baseZIndex={10000}
            />
          )}
        </div>

        <div className={classNames('flex min-h-0 flex-1 flex-col overflow-x-hidden', expanded ? 'px-1 pt-3' : 'p-0')}>
          <div className={classNames('flex flex-none flex-col', expanded ? 'gap-1 px-2' : 'gap-0')}>
            <SidebarNavLink item={items.cases} expanded={expanded} showViewAll />
            {expanded && recentCaseRefs.length > 0 && (
              <div className="hide-scrollbar -mx-2 flex flex-col gap-1 overflow-y-auto px-2 pb-2">
                {recentCaseRefs.map(({ caseRef, chatId }) => (
                  <CaseRefRow
                    key={caseRef}
                    caseRef={caseRef}
                    chatId={chatId}
                    currentSearchParams={currentSearchParams}
                  />
                ))}
              </div>
            )}
            <div className="flex flex-col font-semibold">
              <SidebarNavLink item={items.projects} expanded={expanded} />
            </div>
            {expanded && <hr className="border-theme-border-grey my-2" aria-hidden />}
            <ChatsSectionHeader expanded={expanded} historyHref={historyHref} onNewChat={handleNewChat} />
          </div>

          <div
            ref={setChatsScroller}
            {...{ [CHATS_SCROLLER_ATTR]: '' }}
            className="hide-scrollbar flex min-h-20 flex-1 flex-col overflow-y-auto px-2 pt-1 pb-2"
            onScroll={handleChatsScroll}
            style={chatsScrollMask ? { maskImage: chatsScrollMask, WebkitMaskImage: chatsScrollMask } : undefined}
          >
            <div ref={setChatsList} className="flex flex-col gap-1">
              {expanded && <ChatsSectionList recentChats={recentChats} currentSearchParams={currentSearchParams} />}
            </div>
          </div>

          {/* View all link outside the masked scroll area so the arrow isn't hidden by the fade */}
          {expanded && recentChats.length > 0 && (
            <div className="px-2 pb-2">
              <Link
                to={historyHref}
                className="group focus-visible:outline-fletchers-green-500 text-theme-black hover:!bg-theme-white/90 hover:border-theme-border-grey/50 flex h-8 w-full cursor-pointer items-center rounded-md border border-transparent p-2 text-[0.8125rem] font-semibold transition-shadow hover:shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
              >
                <span className="truncate">View all</span>
                <DownChevronIcon
                  className="ml-auto -rotate-90 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                  width={12}
                  height={12}
                />
              </Link>
            </div>
          )}

          <div
            className={classNames(
              'flex flex-none flex-col',
              expanded ? 'gap-1 px-2 pb-3 font-semibold' : 'gap-0 [&>*:last-child]:border-b-0',
            )}
          >
            {expanded && <hr className="border-theme-border-grey mt-0 mb-2" aria-hidden />}
            {showActions && <SidebarNavLink item={items.actions} expanded={expanded} />}
            <SidebarNavLink item={items.aerials} expanded={expanded} />
            {showUsers && <SidebarNavLink item={items.users} expanded={expanded} />}
            <MermaiddButton expanded={expanded} />
          </div>
        </div>

        <div className={classNames('border-theme-border-grey flex h-14 items-center p-1', expanded && 'border-t')}>
          <UserProfileMenu
            onLogout={handleLogout}
            showIdentity={expanded}
            organisationName={organisationName}
            onOpenChange={setMenuOpen}
          />
        </div>
      </div>
    </nav>
  );
};
